import { exhaustiveCheck } from '../../utils/exhaustive-check';
import { DataContextValue, DataEntities } from './data-provider';

// TODO: add more entity types that can be deleted
type DeletedEntityType = 'account' | 'vendor' | 'evidence' | 'payrollRecord';

type SyncDeleteUpdatesArgs = {
  mutateCache: DataContextValue['mutations']['mutateCache'];
  // These are the entities that are linked to the deleted entities on a 121 basis
  // Add the relevant entities when more deleted entity types are supported
  entityData?: Partial<
    Pick<
      DataEntities,
      'accounts' | 'transactions' | 'vendors' | 'evidence' | 'documents' | 'payrolls' | 'payrollRecords'
    >
  >;
  deletedEntityDetails: {
    entityType: DeletedEntityType;
    entityIds: string[];
  };
};

export const syncDeleteUpdates = async ({ mutateCache, entityData, deletedEntityDetails }: SyncDeleteUpdatesArgs) => {
  if (
    !mutateCache ||
    !entityData ||
    !entityData.accounts ||
    !entityData.transactions ||
    !entityData.vendors ||
    !entityData.evidence ||
    !entityData.documents
  ) {
    return;
  }

  const { accounts, transactions, vendors, evidence, documents, payrolls, payrollRecords } = entityData;

  const { entityType, entityIds } = deletedEntityDetails;

  switch (entityType) {
    case 'account': {
      const remainingAccounts = accounts.filter((account) => !entityIds.includes(account.id));
      const remainingTransactions = transactions.filter((transaction) => !entityIds.includes(transaction.accountId));

      await mutateCache({
        accounts: remainingAccounts,
        transactions: remainingTransactions,
      });

      return;
    }
    case 'vendor': {
      const remainingVendors = vendors.filter((vendor) => !entityIds.includes(vendor.id));
      const remainingTransactions = transactions.filter((transaction) => !entityIds.includes(transaction.vendorId));

      await mutateCache({
        vendors: remainingVendors,
        transactions: remainingTransactions,
      });

      return;
    }
    case 'evidence': {
      const remainingEvidence = evidence.filter((evidence) => !entityIds.includes(evidence.id));
      const remainingDocuments = documents.filter((document) => !entityIds.includes(document.id));

      await mutateCache({
        evidence: remainingEvidence,
        documents: remainingDocuments,
      });

      return;
    }
    case 'payrollRecord': {
      // Remove the deleted payroll records
      const remainingPayrollRecords = payrollRecords?.filter((record) => !entityIds.includes(record.id)) || [];

      // Update payroll objects to remove the deleted records from their records arrays
      const updatedPayrolls =
        payrolls?.map((payroll) => {
          const updatedRecords = payroll.records.filter((record) => !entityIds.includes(record.id));
          return { ...payroll, records: updatedRecords };
        }) || [];

      await mutateCache({
        payrollRecords: remainingPayrollRecords,
        payrolls: updatedPayrolls,
      });

      return;
    }
    default:
      return exhaustiveCheck(entityType);
  }
};
